import type { FetchInputType, FetchInitType } from "./types/fetch-params";
import { SEND_UNAUTHORIZED_EVENT_PATH } from "./config";

// The BigQuery web-event beacon (`window.InnerBalanceClientApi.sendWebEvent`,
// wired up in `ClientApiScript`). This is a SEPARATE telemetry channel from
// Datadog (`useLogEvent`): these events land in BigQuery `ods.prod_web_*`.
// Centralised here so the decision of WHAT reaches the BigQuery error stream
// (`ods.prod_web_errors`) lives in one place — see `useFetchBase`, which only
// calls `sendErrorBeacon` for genuine, unrecovered network failures.

export const REQUEST_BEACON_EVENT_NAME = "request";
// Every error report sent to BigQuery uses this name, not just `sendErrorBeacon`:
// the event service relies on it to keep a failed report from being reported.
export const ERROR_BEACON_EVENT_NAME = "error";

// Fires the per-request beacon (`event_name: "request"`) at the start of every
// API call. No-op for non-string inputs and for the web-event endpoint, which
// delivers the beacon itself and would otherwise beacon about every beacon.
export function sendRequestBeacon(input: FetchInputType, init?: FetchInitType): void {
    if (typeof input !== "string" || input.includes(SEND_UNAUTHORIZED_EVENT_PATH)) {
        return;
    }

    window.InnerBalanceClientApi?.sendWebEvent({
        event_name: REQUEST_BEACON_EVENT_NAME,
        event_type: "user_non_interaction",
        widget: "api",
        meta_data: {
            request_id: Date.now().toString(),
            endpoint: input,
            http_method: init?.method || "GET",
            request_ts: new Date().toISOString(),
        },
    });
}

// Fires the error beacon (`event_name: "error"`) — the one that feeds BigQuery
// `ods.prod_web_errors`. Call ONLY for real errors; benign client-side
// cancellations must NOT reach this channel (see `logFetchCancellation`).
export function sendErrorBeacon(input: FetchInputType, message: string): void {
    // Symmetric with `sendRequestBeacon`: only string inputs produce
    // meaningful telemetry. A URL/Request object would serialize
    // unpredictably (a `Request` → `{}`), so skip it — and stay consistent
    // with the request beacon, which never fired for non-string inputs either.
    if (typeof input !== "string") {
        return;
    }

    window.InnerBalanceClientApi?.sendWebEvent({
        event_name: ERROR_BEACON_EVENT_NAME,
        event_type: "user_non_interaction",
        widget: "api",
        meta_data: {
            url: input,
            error_text: message,
            error_msg_response: message,
        },
    });
}
